import React from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Typography, Chip, TextField, Button, IconButton, Tooltip,
  Select, MenuItem, FormControl, InputLabel, Checkbox, FormControlLabel, CircularProgress
} from '@material-ui/core'
import { ArrowBack as BackIcon, Image as ImageIcon, Videocam as VideoIcon, Mic as MicIcon } from '@material-ui/icons'
import { QUESTION_STATUS_LABELS, QUESTION_STATUS_COLORS, DECISION_TYPE_LABELS, BLOCKER_CATEGORY_LABELS, PRIORITY_LABELS } from '../../constants/contextEnums'
import { subscribe } from '../../../SocketIo/Socket'

const relTime = (d) => {
  if (!d) return ''
  const diff = (Date.now() - new Date(d).getTime()) / 1000
  if (diff < 60) return 'just now'
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return new Date(d).toLocaleDateString()
}

const QuestionThreadPanel = ({ question: initial, url, onBack, onChanged }) => {
  const [question, setQuestion] = React.useState(initial)
  const [replyText, setReplyText] = React.useState('')
  const [sending, setSending] = React.useState(false)
  const [mode, setMode] = React.useState(null) // null | 'resolve' | 'escalate'
  const [resolveForm, setResolveForm] = React.useState({ answer: '', decision_type: 'other', affects_price: false, affects_output: false })
  const [escalateForm, setEscalateForm] = React.useState({ waiting_on_name: '', expected_response_at: '', category: 'other', blocks_output: false })
  const [error, setError] = React.useState('')

  // Socket: keep an open thread current. subscribe() returns a cleanup fn.
  React.useEffect(() => {
    const channel = `estimate_questions_${question.project}`
    const handler = (msg) => {
      if (!msg || String(msg.questionId) !== String(question._id)) return
      if (msg.type === 'reply' && msg.reply) {
        setQuestion(prev => {
          if ((prev.replies || []).some(r => String(r._id) === String(msg.reply._id))) return prev
          return { ...prev, replies: [...(prev.replies || []), msg.reply] }
        })
      } else if (msg.type === 'resolved') {
        setQuestion(prev => ({ ...prev, status: 'answered', resulting_decision_id: msg.decisionId }))
      }
    }
    const unsub = subscribe(channel, handler)
    return () => unsub()
  }, [question._id, question.project])

  const sendReply = () => {
    const body = replyText.trim()
    if (!body) return
    setSending(true)
    setError('')
    axios.post(`${url}/api/bid-estimation/questions/${question._id}/replies`, { body })
      .then(res => {
        setQuestion(prev => {
          if ((prev.replies || []).some(r => String(r._id) === String(res.data._id))) return prev
          return { ...prev, replies: [...(prev.replies || []), res.data] }
        })
        setReplyText('')
        setSending(false)
        if (onChanged) onChanged()
      })
      .catch(err => {
        setSending(false)
        setError(err.response?.data?.error || 'Failed to send reply')
      })
  }

  const submitResolve = () => {
    if (!resolveForm.answer.trim()) { setError('Answer is required'); return }
    setError('')
    axios.put(`${url}/api/bid-estimation/questions/${question._id}/resolve`, resolveForm)
      .then(res => { setQuestion(res.data.question); setMode(null); if (onChanged) onChanged() })
      .catch(err => setError(err.response?.data?.error || 'Failed to resolve'))
  }

  const submitEscalate = () => {
    axios.post(`${url}/api/bid-estimation/questions/${question._id}/escalate`, escalateForm)
      .then(res => { setQuestion(res.data.question); setMode(null); if (onChanged) onChanged() })
      .catch(err => setError(err.response?.data?.error || 'Failed to escalate'))
  }

  const isOpen = question.status === 'open'
  const replies = question.replies || []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 8, borderBottom: '1px solid var(--line, #e0e0e0)' }}>
        <IconButton size="small" onClick={onBack}><BackIcon fontSize="small" /></IconButton>
        <Chip
          label={QUESTION_STATUS_LABELS[question.status] || question.status}
          size="small"
          style={{ height: 20, fontSize: 11, backgroundColor: QUESTION_STATUS_COLORS[question.status] || '#9e9e9e', color: '#fff' }}
        />
        {question.priority && question.priority !== 'normal' && <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{PRIORITY_LABELS[question.priority] || question.priority}</span>}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{question.title}</div>
        {question.body && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{question.body}</div>}

        {/* Thread */}
        <div style={{ marginTop: 12 }}>
          {replies.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-4, #9e9e9e)' }}>No replies yet.</div>}
          {replies.map((r) => (
            <div key={r._id} style={{ padding: '8px 0', borderBottom: '1px solid var(--line-1, #f0f0f0)' }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                <strong>{r.author_name || 'Someone'}</strong> · {relTime(r.createdAt)}
              </div>
              <div style={{ fontSize: 13, marginTop: 2 }}>{r.body}</div>
            </div>
          ))}
        </div>
      </div>

      {error && <Typography variant="caption" style={{ color: 'var(--risk, #d32f2f)' }}>{error}</Typography>}

      {/* Resolve form */}
      {mode === 'resolve' && (
        <div style={{ borderTop: '1px solid var(--line, #e0e0e0)', paddingTop: 8 }}>
          <TextField label="Answer" required fullWidth size="small" multiline rows={2} value={resolveForm.answer}
            onChange={e => setResolveForm(f => ({ ...f, answer: e.target.value }))} margin="dense" inputProps={{ maxLength: 2000 }} />
          <FormControl fullWidth size="small" margin="dense">
            <InputLabel>Decision Type</InputLabel>
            <Select value={resolveForm.decision_type} label="Decision Type"
              onChange={e => setResolveForm(f => ({ ...f, decision_type: e.target.value }))}>
              {Object.entries(DECISION_TYPE_LABELS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel control={<Checkbox size="small" checked={resolveForm.affects_price}
            onChange={e => setResolveForm(f => ({ ...f, affects_price: e.target.checked }))} />} label="Affects price" />
          <FormControlLabel control={<Checkbox size="small" checked={resolveForm.affects_output}
            onChange={e => setResolveForm(f => ({ ...f, affects_output: e.target.checked }))} />} label="Affects output" />
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <Button size="small" onClick={() => setMode(null)}>Cancel</Button>
            <Button size="small" variant="contained" color="primary" onClick={submitResolve}>Create Decision</Button>
          </div>
        </div>
      )}

      {/* Escalate form */}
      {mode === 'escalate' && (
        <div style={{ borderTop: '1px solid var(--line, #e0e0e0)', paddingTop: 8 }}>
          <TextField label="Waiting on" fullWidth size="small" value={escalateForm.waiting_on_name}
            onChange={e => setEscalateForm(f => ({ ...f, waiting_on_name: e.target.value }))} margin="dense" />
          <TextField label="Expected Response" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }}
            value={escalateForm.expected_response_at}
            onChange={e => setEscalateForm(f => ({ ...f, expected_response_at: e.target.value }))} margin="dense" />
          <FormControl fullWidth size="small" margin="dense">
            <InputLabel>Category</InputLabel>
            <Select value={escalateForm.category} label="Category"
              onChange={e => setEscalateForm(f => ({ ...f, category: e.target.value }))}>
              {Object.entries(BLOCKER_CATEGORY_LABELS).map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel control={<Checkbox size="small" checked={escalateForm.blocks_output}
            onChange={e => setEscalateForm(f => ({ ...f, blocks_output: e.target.checked }))} />} label="Blocks output" />
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            <Button size="small" onClick={() => setMode(null)}>Cancel</Button>
            <Button size="small" variant="contained" color="primary" onClick={submitEscalate}>Create Blocker</Button>
          </div>
        </div>
      )}

      {/* Composer + actions */}
      {mode === null && (
        <div style={{ borderTop: '1px solid var(--line, #e0e0e0)', paddingTop: 8 }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end' }}>
            <TextField placeholder="Add a reply..." fullWidth size="small" multiline rowsMax={4}
              value={replyText} onChange={e => setReplyText(e.target.value)} />
            <Tooltip title="Image upload — coming soon"><span><IconButton size="small" disabled><ImageIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Video upload — coming soon"><span><IconButton size="small" disabled><VideoIcon fontSize="small" /></IconButton></span></Tooltip>
            <Tooltip title="Audio upload — coming soon"><span><IconButton size="small" disabled><MicIcon fontSize="small" /></IconButton></span></Tooltip>
            <Button size="small" variant="contained" color="primary" disabled={sending || !replyText.trim()} onClick={sendReply}>
              {sending ? <CircularProgress size={14} /> : 'Send'}
            </Button>
          </div>
          {isOpen && (
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <Button size="small" variant="outlined" onClick={() => { setError(''); setMode('resolve') }}>Resolve &amp; Create Decision</Button>
              <Button size="small" variant="outlined" disabled={!!question.escalated_blocker_id}
                onClick={() => { setError(''); setMode('escalate') }}>Escalate to Blocker</Button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({ url: state.url })
export default connect(mapStateToProps)(QuestionThreadPanel)
